import { Box, DialogContent, Divider, SvgIcon, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { endOfDay, startOfDay, subDays } from 'date-fns'
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { useForm, FormProvider } from 'react-hook-form'

import { getGroupTitle } from '../../../tanning-history.utils'

import DialogLayout from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import CheckCircleSmallIcon from '@hapana/shared/web/styles/assets/svgs/check-circle-small.svg'
import ExclamationTriangleSmallIcon from '@hapana/shared/web/styles/assets/svgs/exclamation-triangle-small.svg'
import SyncSmallIcon from '@hapana/shared/web/styles/assets/svgs/sync-small.svg'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import useSiteStore from '_stores/site.store'
import { useGetSiteById } from '_hooks/gqty/sites.hooks'
import DateRangePicker, {
  START_DATE,
} from '@hapana/shared/web/components/shared/date-picker/date-range-picker/date-range-picker.component'

import { useGetEquipment } from '_hooks/equipments.hooks'
import { EQUIPMENT_ASSIGNMENT_STATUSES } from '_constants/equipments'

import { styles, handleStylesWithProps } from './test-log.styles'

const StatusIcon = ({ status }) => (
  <Box
    sx={[
      styles.iconWrapper,
      status === EQUIPMENT_ASSIGNMENT_STATUSES.DONE
        ? styles.iconWrapperSuccess
        : status === EQUIPMENT_ASSIGNMENT_STATUSES.CANCELLED
          ? styles.iconWrapperError
          : styles.iconWrapperOngoing,
    ]}
  >
    <SvgIcon
      component={
        status === EQUIPMENT_ASSIGNMENT_STATUSES.DONE
          ? CheckCircleSmallIcon
          : status === EQUIPMENT_ASSIGNMENT_STATUSES.CANCELLED
            ? ExclamationTriangleSmallIcon
            : SyncSmallIcon
      }
      sx={handleStylesWithProps(status).statusIcon}
    />
  </Box>
)

StatusIcon.propTypes = {
  status: PropTypes.oneOf([
    EQUIPMENT_ASSIGNMENT_STATUSES.CREATED,
    EQUIPMENT_ASSIGNMENT_STATUSES.DONE,
    EQUIPMENT_ASSIGNMENT_STATUSES.CANCELLED,
  ]).isRequired,
}

export const EquipmentTestLogDialog = ({ equipmentId, equipmentName }) => {
  const { t } = useTranslation(['checkIn'])
  const { closeDialog } = useDialog()
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { data: siteData } = useGetSiteById(siteId)
  const siteTimezone = siteData?.timezone
  const formMethods = useForm()

  const todayInSiteTimezone = useMemo(() => {
    const now = new Date()
    if (!siteTimezone) return now
    const year = parseInt(formatInTimeZone(now, siteTimezone, 'yyyy'), 10)
    const month = parseInt(formatInTimeZone(now, siteTimezone, 'MM'), 10) - 1
    const day = parseInt(formatInTimeZone(now, siteTimezone, 'dd'), 10)
    return new Date(year, month, day)
  }, [siteTimezone])

  const initialStart = useMemo(
    () => zonedTimeToUtc(startOfDay(subDays(todayInSiteTimezone, 7)), siteTimezone || 'UTC'),
    [todayInSiteTimezone, siteTimezone]
  )
  const initialEnd = useMemo(
    () => zonedTimeToUtc(endOfDay(todayInSiteTimezone), siteTimezone || 'UTC'),
    [todayInSiteTimezone, siteTimezone]
  )

  const [filters, setFilters] = useState({
    startDate: initialStart,
    endDate: initialEnd,
  })

  const timezoneInitialized = useRef(false)
  useEffect(() => {
    if (!siteTimezone || timezoneInitialized.current) return
    timezoneInitialized.current = true
    setFilters((prev) => ({ ...prev, startDate: initialStart, endDate: initialEnd }))
  }, [siteTimezone, initialStart, initialEnd])

  const { data: equipmentData, isLoading, refetch } = useGetEquipment(siteId, equipmentId)

  useEffect(() => {
    refetch()
  }, [filters, refetch])

  const assignments = useMemo(() => {
    const all = equipmentData?.equipment?.assignments || []
    const startMs = filters.startDate ? filters.startDate.getTime() : null
    const endMs = filters.endDate ? filters.endDate.getTime() : null

    const testOnly = all.filter((a) => !a.clientId)

    return testOnly.filter((a) => {
      if (startMs == null || endMs == null) return true
      const createdMs = a?.createdAt ? Number(a.createdAt) : null
      return createdMs !== null && createdMs >= startMs && createdMs <= endMs
    })
  }, [equipmentData, filters.startDate, filters.endDate])

  const groups = useMemo(() => {
    const tz = siteTimezone || 'UTC'
    const byDate = {}
    assignments.forEach((a) => {
      const createdAt = a?.createdAt ? new Date(Number(a.createdAt)) : null
      if (!createdAt) return
      const key = formatInTimeZone(createdAt, tz, 'yyyy-MM-dd')
      if (!byDate[key]) byDate[key] = []

      let canonical
      let finishedDate = null

      if (a?.doneAt) {
        canonical = EQUIPMENT_ASSIGNMENT_STATUSES.DONE
        finishedDate = new Date(Number(a.doneAt))
      } else if (a?.cancelledAt) {
        canonical = EQUIPMENT_ASSIGNMENT_STATUSES.CANCELLED
        finishedDate = new Date(Number(a.cancelledAt))
      } else {
        canonical = EQUIPMENT_ASSIGNMENT_STATUSES.CREATED
      }

      const startedStr = formatInTimeZone(createdAt, tz, 'h:mm a')
      const finishedStr = finishedDate ? formatInTimeZone(finishedDate, tz, 'h:mm a') : null

      byDate[key].push({
        status: canonical,
        startedAt: startedStr,
        finishedAt: finishedStr,
      })
    })

    return Object.keys(byDate)
      .sort((a, b) => (a > b ? -1 : 1))
      .map((dateKey) => ({ date: dateKey, items: byDate[dateKey] }))
  }, [assignments, siteTimezone])

  const handleDateChange = (field, value) => {
    const tz = siteTimezone || 'UTC'
    const calDay = new Date(value.getFullYear(), value.getMonth(), value.getDate())
    if (field === START_DATE) {
      setFilters((prev) => ({
        ...prev,
        startDate: zonedTimeToUtc(startOfDay(calDay), tz),
        endDate: zonedTimeToUtc(endOfDay(calDay), tz),
      }))
    } else {
      setFilters((prev) => ({
        ...prev,
        endDate: zonedTimeToUtc(endOfDay(calDay), tz),
      }))
    }
  }

  const formatGroupTitle = (dateString) =>
    getGroupTitle(
      dateString,
      t,
      {
        todayKey: 'checkIn:equipments.testLog.today',
        yesterdayKey: 'checkIn:equipments.testLog.yesterday',
      },
      siteTimezone
    )

  return (
    <FormProvider {...formMethods}>
      <DialogLayout
        closeType={CLOSE_TYPES.ICON}
        closeAction={closeDialog}
        title={t('checkIn:equipments.detail.viewTestLog')}
        size={SIZES.SM}
        hasBackLabel
      >
        <DialogContent sx={styles.dialogContent}>
          <Box sx={styles.content}>
            <Typography typography="header2" color="gray.80">
              {t('checkIn:equipments.testLog.title', { equipment: equipmentName })}
            </Typography>

            <Box sx={styles.dateFilters}>
              <DateRangePicker
                key={`${filters.startDate?.getTime()}-${filters.endDate?.getTime()}`}
                initialStartDate={utcToZonedTime(filters.startDate, siteTimezone || 'UTC')}
                initialEndDate={utcToZonedTime(filters.endDate, siteTimezone || 'UTC')}
                onDateChange={(field, value) => handleDateChange(field, value)}
                maxDate={zonedTimeToUtc(
                  `${formatInTimeZone(new Date(), siteTimezone || 'UTC', 'yyyy-MM-dd')}T23:59:59.999`,
                  siteTimezone || 'UTC'
                )}
                layout="horizontal"
                displayFormat="EEE MMM dd yyyy"
                timezone={siteTimezone}
              />
            </Box>

            {isLoading ? (
              <Box sx={styles.loadingContainer}>
                <Loading />
              </Box>
            ) : (
              groups.map((group, index) => (
                <Box key={group.date} sx={styles.groupContainer}>
                  <Box sx={styles.groupHeader}>
                    <Typography typography="medium" color="gray.64">
                      {formatGroupTitle(group.date)}
                    </Typography>
                    <Typography typography="medium" color="gray.64">
                      {t('checkIn:equipments.testLog.tests', { count: group.items.length })}
                    </Typography>
                  </Box>

                  <Box sx={styles.list}>
                    {group.items.map((item, iIdx) => (
                      <Box key={`${group.date}-${iIdx}`} sx={styles.listItem}>
                        <Box sx={styles.left}>
                          <StatusIcon status={item.status} />
                          <Box sx={styles.details}>
                            <Typography typography="header3" color="gray.100">
                              {item.status === EQUIPMENT_ASSIGNMENT_STATUSES.DONE
                                ? t('checkIn:equipments.testLog.successfulTest')
                                : item.status === EQUIPMENT_ASSIGNMENT_STATUSES.CANCELLED
                                  ? t('checkIn:equipments.testLog.errorTest')
                                  : t('checkIn:equipments.testLog.ongoingTest')}
                            </Typography>
                            <Typography typography="medium" sx={styles.times}>
                              {t('checkIn:equipments.testLog.startedAt', { time: item.startedAt })}
                              {' — '}
                              {item.finishedAt
                                ? t('checkIn:equipments.testLog.finishedAt', {
                                    time: item.finishedAt,
                                  })
                                : t('checkIn:equipments.testLog.inProgress')}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  {index !== groups.length - 1 && <Divider sx={styles.divider} />}
                </Box>
              ))
            )}
            {!isLoading && groups.length === 0 && (
              <Typography typography="medium" color="gray.64" sx={styles.emptyMessage}>
                {t('checkIn:equipments.testLog.noTests')}
              </Typography>
            )}
          </Box>
        </DialogContent>
      </DialogLayout>
    </FormProvider>
  )
}

EquipmentTestLogDialog.propTypes = {
  equipmentId: PropTypes.string.isRequired,
  equipmentName: PropTypes.string.isRequired,
}

export default EquipmentTestLogDialog
